import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { CustomerDetailDto } from "@grizzly/shared";
import { api } from "../lib/apiClient.js";
import type { ApiClientError } from "../lib/apiClient.js";
import { useI18n } from "../i18n/index.js";
import { ErrorState, Skeleton } from "../components/ui/states.js";
import { useToast } from "../lib/useToast.js";
import { ContactSection } from "../components/crm/contactSection.js";
import { AddressSection } from "../components/crm/addressSection.js";
import { TaskSection } from "../components/crm/taskSection.js";
import { ActivityTimeline } from "../components/crm/activityTimeline.js";
import { CustomerHeader } from "../components/crm/customerHeader.js";

export function CustomerDetailPage() {
  const { id = "" } = useParams();
  const { push } = useToast();
  const { t } = useI18n();
  const [customer, setCustomer] = useState<CustomerDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(
    null
  );
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getCustomer(id);
      setCustomer(res.customer);
    } catch (err) {
      const e = err as ApiClientError;
      setError({ message: e.message, requestId: e.requestId });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleArchive = useCallback(async () => {
    if (!customer) return;
    setBusy(true);
    try {
      if (customer.status === "archived") {
        await api.unarchiveCustomer(customer.id);
        push(t("customers.reactivatedToast"));
      } else {
        await api.archiveCustomer(customer.id);
        push(t("customers.archivedToast"));
      }
      await load();
    } catch (err) {
      const e = err as ApiClientError;
      push(
        e.message,
        e.requestId ? `${t("common.requestId", { id: e.requestId })}` : undefined,
        "danger"
      );
    } finally {
      setBusy(false);
    }
  }, [customer, load, push, t]);

  if (loading) {
    return <Skeleton rows={6} />;
  }
  if (error || !customer) {
    return (
      <ErrorState
        message={error?.message ?? t("customers.notFound")}
        requestId={error?.requestId}
      />
    );
  }

  return (
    <>
      <CustomerHeader
        customer={customer}
        busy={busy}
        onToggleArchive={() => void toggleArchive()}
      />
      <div className="grid gap-4 xl:grid-cols-2">
        <div className="space-y-4">
          <ContactSection customer={customer} onChanged={() => void load()} />
          <AddressSection customer={customer} onChanged={() => void load()} />
        </div>
        <div className="space-y-4">
          <TaskSection customerId={customer.id} />
          <ActivityTimeline customerId={customer.id} />
        </div>
      </div>
    </>
  );
}
